(function () {
  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setupAutoplay = (slider) => {
    const cards = slider.querySelectorAll('[data-pdp-ts-autoplay-card]');
    if (!cards.length) return;
    if (!('IntersectionObserver' in window) || prefersReducedMotion()) return;

    const maxConcurrent = Math.max(1, parseInt(slider.dataset.maxConcurrentAutoplay, 10) || 2);
    const states = new Map();
    const track = slider.querySelector('[data-pdp-ts-track]');
    let scrollDir = 0;
    let lastScrollLeft = track ? track.scrollLeft : 0;
    let scrollEndTimer = null;

    const findIncoming = (dir) => {
      if (!dir) return null;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      let pick = null;
      let pickDist = Infinity;
      states.forEach((s, card) => {
        if (!s.nearby || s.intersecting) return;
        if (card.classList.contains('is-unmuted')) return;
        const r = card.getBoundingClientRect();
        const dist = dir > 0 ? r.left - vw : -r.right;
        if (dist < 0) return;
        if (dist < pickDist) {
          pickDist = dist;
          pick = card;
        }
      });
      return pick;
    };

    const ensureSrc = (video, card) => {
      if (video.src || video.currentSrc) return;
      const src = card.dataset.videoSrc;
      if (!src) return;
      video.preload = 'auto';
      video.src = src;
      video.load();
    };

    const attemptPlay = (video, card) => {
      const state = states.get(card);
      if (!state || !state.wanted) return;
      video.muted = true;
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          if (!state.wanted) return;
          const retry = () => {
            video.removeEventListener('canplay', retry);
            const s = states.get(card);
            if (!s || !s.wanted) return;
            const p2 = video.play();
            if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
          };
          video.addEventListener('canplay', retry, { once: true });
        });
      }
    };

    const update = () => {
      const nearby = [...states.entries()].filter(
        ([card, s]) => (s.intersecting || s.nearby) && !card.classList.contains('is-unmuted')
      );

      const playing = nearby
        .sort((a, b) => {
          const aScore = a[1].intersecting ? 1 + a[1].ratio : a[1].ratio;
          const bScore = b[1].intersecting ? 1 + b[1].ratio : b[1].ratio;
          return bScore - aScore;
        })
        .slice(0, maxConcurrent)
        .map(([card]) => card);
      const playingSet = new Set(playing);

      const incoming = findIncoming(scrollDir);
      if (incoming && states.has(incoming) && !playingSet.has(incoming)) {
        playingSet.add(incoming);
      }

      nearby.forEach(([card]) => {
        const video = card.querySelector('.pdp-ts__video');
        if (video) ensureSrc(video, card);
      });

      if (incoming) {
        const v = incoming.querySelector('.pdp-ts__video');
        if (v) ensureSrc(v, incoming);
      }

      states.forEach((state, card) => {
        if (card.classList.contains('is-unmuted')) return;
        const video = card.querySelector('.pdp-ts__video');
        if (!video) return;
        if (playingSet.has(card)) {
          state.wanted = true;
          if (video.paused) attemptPlay(video, card);
        } else {
          state.wanted = false;
          if (!video.paused) video.pause();
        }
      });
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const state = states.get(entry.target);
          if (state) {
            state.intersecting = entry.isIntersecting;
            state.ratio = entry.intersectionRatio;
          }
        });
        update();
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    const ioPrefetch = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const state = states.get(entry.target);
          if (state) state.nearby = entry.isIntersecting;
        });
        update();
      },
      { threshold: 0, rootMargin: '0px 100% 0px 100%' }
    );

    cards.forEach((card) => {
      const video = card.querySelector('.pdp-ts__video');
      if (video) {
        video.addEventListener('playing', () => card.classList.add('is-playing'));
      }
      states.set(card, { intersecting: false, ratio: 0, nearby: false, wanted: false });
      io.observe(card);
      ioPrefetch.observe(card);

      const rect = card.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const inView =
        rect.top < vh && rect.bottom > 0 && rect.left < vw && rect.right > 0;
      const nearby =
        rect.top < vh && rect.bottom > 0 && rect.left < vw * 2 && rect.right > -vw;
      if (inView || nearby) {
        const s = states.get(card);
        s.intersecting = inView;
        s.ratio = inView ? 0.5 : 0;
        s.nearby = nearby;
      }

      const unmuteBtn = card.querySelector('[data-pdp-ts-unmute]');
      if (unmuteBtn && video) {
        unmuteBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          card.classList.add('is-unmuted');
          ensureSrc(video, card);
          video.muted = false;
          video.controls = true;
          video.removeAttribute('tabindex');
          video.removeAttribute('aria-hidden');
          const p = video.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
          io.unobserve(card);
          ioPrefetch.unobserve(card);
          states.delete(card);
          update();
        });
      }
    });

    if (track) {
      track.addEventListener(
        'scroll',
        () => {
          const sl = track.scrollLeft;
          const dir = sl > lastScrollLeft ? 1 : sl < lastScrollLeft ? -1 : 0;
          lastScrollLeft = sl;
          if (dir) {
            scrollDir = dir;
            update();
          }
          clearTimeout(scrollEndTimer);
          scrollEndTimer = setTimeout(() => {
            scrollDir = 0;
            update();
          }, 180);
        },
        { passive: true }
      );
    }

    update();

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        states.forEach((_s, card) => {
          if (card.classList.contains('is-unmuted')) return;
          const v = card.querySelector('.pdp-ts__video');
          if (v && !v.paused) v.pause();
        });
      } else {
        update();
      }
    });
  };

  const initSlider = (slider) => {
    if (slider.dataset.pdpTsInit) return;
    slider.dataset.pdpTsInit = '1';

    const track = slider.querySelector('[data-pdp-ts-track]');
    const prevBtn = slider.querySelector('[data-pdp-ts-prev]');
    const nextBtn = slider.querySelector('[data-pdp-ts-next]');
    if (!track) return;

    const scrollAmount = () => {
      const firstSlide = track.querySelector('.pdp-ts__slide');
      if (!firstSlide) return track.clientWidth;
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return firstSlide.getBoundingClientRect().width + gap;
    };

    const updateArrows = () => {
      if (!prevBtn || !nextBtn) return;
      const maxScroll = track.scrollWidth - track.clientWidth;
      prevBtn.disabled = track.scrollLeft <= 1;
      nextBtn.disabled = track.scrollLeft >= maxScroll - 1;
    };

    // Begin buffering the autoplay videos at/around a future scroll position.
    // Runs ONLY on user interaction (hover / arrow click) so it never adds to
    // initial page load — keeps PDP PSI/LCP unaffected while making arrow
    // navigation feel instant instead of black-screen-then-buffer. Guarded so a
    // video is only loaded once (mirrors setupAutoplay's ensureSrc).
    const preloadNearViewport = (lookaheadPx) => {
      const trackRect = track.getBoundingClientRect();
      const minX = trackRect.left - lookaheadPx;
      const maxX = trackRect.right + lookaheadPx;
      track.querySelectorAll('.pdp-ts__card--autoplay').forEach((card) => {
        const r = card.getBoundingClientRect();
        if (r.right < minX || r.left > maxX) return;
        const video = card.querySelector('.pdp-ts__video');
        const src = card.dataset.videoSrc;
        if (!video || !src || video.src || video.currentSrc) return;
        video.preload = 'auto';
        video.src = src;
        video.load();
      });
    };

    // Warm up neighbours as soon as the user shows intent to navigate (desktop
    // hover — arrows are hidden on mobile), so the first arrow click already has
    // a buffered video ready rather than starting the fetch on arrival.
    slider.addEventListener('pointerenter', () => preloadNearViewport(track.clientWidth * 1.5));

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        preloadNearViewport(track.clientWidth * 2);
        track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        preloadNearViewport(track.clientWidth * 2);
        track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
      });
    }

    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    updateArrows();

    setupAutoplay(slider);

    track.querySelectorAll('[data-pdp-ts-play]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const card = btn.closest('.pdp-ts__card');
        if (!card || card.classList.contains('is-playing')) return;

        const youtubeId = card.dataset.youtubeId;
        const videoFile = card.dataset.videoFile;
        const media = card.querySelector('.pdp-ts__media');
        if (!media) return;

        let mediaEl;
        if (videoFile) {
          mediaEl = document.createElement('video');
          mediaEl.src = videoFile;
          mediaEl.controls = true;
          mediaEl.autoplay = true;
          mediaEl.playsInline = true;
        } else if (youtubeId) {
          mediaEl = document.createElement('iframe');
          mediaEl.src = `https://www.youtube.com/embed/${encodeURIComponent(youtubeId)}?autoplay=1&rel=0&playsinline=1`;
          mediaEl.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
          mediaEl.allowFullscreen = true;
          mediaEl.title = 'Testimonial video';
        } else {
          return;
        }

        media.appendChild(mediaEl);
        card.classList.add('is-playing');
      });
    });
  };

  const initAll = () => {
    document.querySelectorAll('[data-pdp-ts-slider]').forEach(initSlider);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelectorAll) {
      e.target.querySelectorAll('[data-pdp-ts-slider]').forEach(initSlider);
    }
  });
})();
